import { Phone, Mail, MapPin, Clock, ExternalLink } from 'lucide-react'
import ContactForm from '../components/ContactForm'
import SectionHeader from '../components/SectionHeader'
import { SITE, CONTACT } from '../content/siteContent'

export default function ContactPage() {
  return (
    <main>
      {/* Page hero */}
      <section className="bg-gradient-to-br from-brand-navy to-brand-blue pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }} aria-hidden="true" />
        <div className="section-container relative z-10 text-center">
          <p className="text-brand-amber text-xs font-round font-bold tracking-[0.15em] uppercase mb-3">Reach Out</p>
          <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">{CONTACT.headline}</h1>
          <p className="text-slate-200 text-lg max-w-xl mx-auto">{CONTACT.subheadline}</p>
        </div>
        <div className="absolute bottom-0 left-0 right-0" aria-hidden="true">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" preserveAspectRatio="none">
            <path d="M0 60V30C240 0 480 50 720 30C960 10 1200 50 1440 30V60H0Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* Main contact section */}
      <section className="section-pad bg-white">
        <div className="section-container">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-16">

            {/* Info panel */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-brand-navy font-display font-bold text-2xl mb-6">Contact Information</h2>
                <ul className="space-y-5" role="list">
                  <li>
                    <a
                      href={`tel:${SITE.contact.phone.replace(/\D/g,'')}`}
                      className="flex items-center gap-4 group"
                    >
                      <div className="w-12 h-12 rounded-xl2 bg-brand-sky/15 flex items-center justify-center text-brand-blue group-hover:bg-brand-blue group-hover:text-white transition-colors flex-shrink-0">
                        <Phone size={20} aria-hidden="true" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-0.5">Phone</p>
                        <p className="text-brand-navy font-bold">{SITE.contact.phone}</p>
                      </div>
                    </a>
                  </li>
                  <li>
                    <a
                      href={`mailto:${SITE.contact.email}`}
                      className="flex items-center gap-4 group"
                    >
                      <div className="w-12 h-12 rounded-xl2 bg-brand-sky/15 flex items-center justify-center text-brand-blue group-hover:bg-brand-blue group-hover:text-white transition-colors flex-shrink-0">
                        <Mail size={20} aria-hidden="true" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-0.5">Email</p>
                        <p className="text-brand-navy font-bold text-sm break-all">{SITE.contact.email}</p>
                      </div>
                    </a>
                  </li>
                  <li>
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(SITE.contact.address.display)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-4 group"
                    >
                      <div className="w-12 h-12 rounded-xl2 bg-brand-sky/15 flex items-center justify-center text-brand-blue group-hover:bg-brand-blue group-hover:text-white transition-colors flex-shrink-0">
                        <MapPin size={20} aria-hidden="true" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-0.5">Address</p>
                        <p className="text-brand-navy font-bold text-sm">{SITE.contact.address.display}</p>
                        <p className="text-brand-blue text-xs mt-0.5 flex items-center gap-1">
                          Open in Maps <ExternalLink size={11} aria-hidden="true" />
                        </p>
                      </div>
                    </a>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl2 bg-brand-sky/15 flex items-center justify-center text-brand-blue flex-shrink-0">
                      <Clock size={20} aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-0.5">Hours</p>
                      <p className="text-brand-navy font-bold">{SITE.hours.days}</p>
                      <p className="text-slate-500 text-sm">{SITE.hours.open} – {SITE.hours.close}</p>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Map placeholder */}
              <div className="rounded-xl3 overflow-hidden shadow-card">
                {/*
                  TODO: Replace with a real Google Maps embed.
                  To embed: Go to Google Maps, search the address, click Share → Embed a map, copy the iframe src.
                  You can also set up Google Maps JavaScript API for a fully interactive map.
                */}
                <a
                  href={CONTACT.mapPlaceholder}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block relative bg-gradient-to-br from-brand-sky/20 to-brand-blue/10 p-12 text-center hover:from-brand-sky/30 transition-colors"
                  aria-label="Open address in Google Maps"
                >
                  <MapPin size={40} className="text-brand-blue mx-auto mb-3" aria-hidden="true" />
                  <p className="font-semibold text-brand-navy mb-1">10951 E CR 100 S.</p>
                  <p className="text-slate-500 text-sm mb-3">Indianapolis, IN 46231</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:underline">
                    Open in Google Maps <ExternalLink size={14} aria-hidden="true" />
                  </span>
                </a>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="card">
                <SectionHeader eyebrow="Inquire Now" headline="Send Us a Message" subheadline="Fill out the form below and we'll get back to you promptly." centered={false} />
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}
